import React, { useState, useEffect } from "react";
import {
    Button,
    Box,
    TextField,
    Card,
    CardContent,
    Typography,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { buttonPrimaryStyles, sectionBox } from "../styles/ComponentStyles";
import { userLogin } from "../api/login";
import { forgotPassword } from "../api/userPassword";
import { useTranslate } from "../components/Translate/TranslateContext";
import AnimatedIcons from "../components/Layout/AnimatedIcons";

export default function Login({ onLoginSuccess }) {
    const { translate, language } = useTranslate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resetMode, setResetMode] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [showError, setShowError] = useState(false);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    useEffect(() => {
        if (!statusMessage) return;
        const timer = setTimeout(() => setStatusMessage(""), 2500);
        return () => clearTimeout(timer);
    }, [statusMessage]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !password) {
            setShowError(true);
            setTimeout(() => setShowError(false), 2500);
            return;
        }

        setLoading(true);
        try {
            const data = await userLogin(email, password, remember, language);

            if (data?.success) {
                onLoginSuccess(data);
            } else if (data?.message === "verify_device") {
                setStatusMessage(translate("verify_device_message"));
            } else if (data?.message === "user_not_found") {
                setStatusMessage(translate("user_not_found"));
            } else if (data?.message === "invalid_credentials") {
                setStatusMessage(translate("invalid_credentials"));
            } else {
                setStatusMessage(translate("login_failed"));
            }
        } catch {
            setStatusMessage(translate("server_error"));
        } finally {
            setLoading(false);
            setTimeout(() => setStatusMessage(""), 2500);
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        try {
            const data = await forgotPassword(email, language);
            if (data?.success) {
                setStatusMessage(translate("reset_link_sent"));
            } else {
                setStatusMessage(translate("reset_failed"));
            }
        } catch {
            setStatusMessage(translate("server_error"));
        } finally {
            setLoading(false);
            setTimeout(() => setStatusMessage(""), 3000);
        }
    };

    return (
        <Box
            sx={{
                ...sectionBox,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                minHeight: "calc(100vh - 70px)",
                mt: "auto",
                px: 2,
                py: 4,
            }}
        >
            <AnimatedIcons />
            <Card
                sx={{
                    maxWidth: 420,
                    width: "100%",
                    textAlign: "center",
                    boxShadow: 6,
                    zIndex: 2,
                    borderRadius: 3,
                    mx: "auto",
                    "@media (max-width:600px)": {
                        maxWidth: "100%",
                        boxShadow: 2,
                        p: 2,
                    },
                }}
            >
                <CardContent
                    sx={{
                        p: { xs: 3, sm: 4 },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <a
                            href="https://tensoai.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: "inline-flex" }}
                        >
                            <img
                                src="/tensoLogo.png"
                                alt="TensoAI Logo"
                                style={{
                                    width: "64px",
                                    height: "64px",
                                    objectFit: "contain",
                                    cursor: "pointer",
                                }}
                            />
                        </a>
                    </Box>

                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontSize: { xs: "1.4rem", sm: "1.6rem" },
                            wordBreak: "break-word",
                        }}
                    >
                        {translate("Farmsync")}
                    </Typography>

                    {!resetMode ? (
                        <form
                            onSubmit={handleSubmit}
                            style={{ width: "100%", maxWidth: "350px" }}
                        >
                            <TextField
                                label={translate("email")}
                                fullWidth
                                margin="normal"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={showError && !email}
                                helperText={showError && !email ? translate("email_error") : ""}
                                InputProps={{ sx: { fontSize: { xs: "0.9rem", sm: "1rem" } } }}
                            />

                            <TextField
                                label={translate("password")}
                                type="password"
                                fullWidth
                                margin="normal"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                error={showError && !password}
                                helperText={
                                    showError && !password ? translate("password_error") : ""
                                }
                                InputProps={{ sx: { fontSize: { xs: "0.9rem", sm: "1rem" } } }}
                            />

                            <Box
                                sx={{
                                    textAlign: "right",
                                    mt: 1,
                                    cursor: "pointer",
                                    color: "primary.main",
                                    fontSize: { xs: 13, sm: 14 },
                                }}
                                onClick={() => setResetMode(true)}
                            >
                                {translate("forgot_password")}
                            </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={loading}
                                sx={{ ...buttonPrimaryStyles, mt: 2 }}
                            >
                                {loading ? translate("logging_in") : translate("login")}
                            </Button>

                            {statusMessage && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "red",
                                        mt: 1,
                                        fontWeight: 500,
                                        textAlign: "center",
                                        opacity: statusMessage ? 1 : 0,
                                        transition: "opacity 0.3s ease",
                                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                    }}
                                >
                                    {statusMessage}
                                </Typography>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={remember}
                                        onChange={(e) => setRemember(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={translate("remember_me")}
                                sx={{
                                    mt: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    "& .MuiTypography-root": {
                                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                    },
                                }}
                            />
                        </form>
                    ) : (
                        <form
                            onSubmit={handleReset}
                            style={{ width: "100%", maxWidth: "350px" }}
                        >
                            <TextField
                                label={translate("email")}
                                fullWidth
                                margin="normal"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={!isValidEmail}
                                sx={{ ...buttonPrimaryStyles, mt: 2 }}
                            >
                                {translate("send_reset_link")}
                            </Button>

                            {statusMessage && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 1,
                                        color: statusMessage.includes("verify")
                                            ? "primary.main"
                                            : "error.main",
                                        opacity: statusMessage ? 1 : 0,
                                        transition: "opacity 0.3s ease",
                                        fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                    }}
                                >
                                    {statusMessage}
                                </Typography>
                            )}

                            <Typography
                                variant="body2"
                                sx={{
                                    mt: 2,
                                    cursor: "pointer",
                                    color: "text.secondary",
                                    textDecoration: "underline",
                                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                                }}
                                onClick={() => setResetMode(false)}
                            >
                                {translate("login")}
                            </Typography>
                        </form>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
